/**
 * A integração da marca com o ActiveCampaign (A172, 15/10/2026): a resposta
 * de um formulário vira contato e negócio num funil do CRM da marca.
 *
 * Três coleções:
 *
 * - `conexoesActiveCampaign` (id == id da marca): a URL da API e a chave,
 *   cifrada como a da Cativa (`EncryptedValue`, chave de cifra
 *   `ACTIVECAMPAIGN_SECRET_ENCRYPTION_KEY` no Secret Manager). Fora do
 *   documento da marca porque `GET /api/partners/branding` é público. A chave
 *   nunca sai em resposta: use `paraSituacaoDaConexaoActiveCampaign()`.
 * - `regrasActiveCampaign`: "o formulário X vai para o funil Y, na etapa Z",
 *   com o mapa de qual campo do CRM recebe o quê. Uma regra por formulário.
 * - `enviosActiveCampaign` (id == id da resposta): o que aconteceu com cada
 *   resposta — enviado (com o negócio criado) ou erro (com o motivo), para a
 *   marca ver e reenviar. Num funil de vendas, falha silenciosa é indicação
 *   perdida. Só guarda ids e o motivo do erro, nada pessoal.
 */
import { z } from 'zod';
import { brandIdField } from './brand';
import { EncryptedValueSchema } from './calendly-connection.schema';

// --- Conexão -----------------------------------------------------------------

export const ConexaoActiveCampaignSchema = z.object({
  id: z.string(),
  brandId: brandIdField(),
  /** Só a origem, como `https://conta.api-us1.com` (sem `/api/3`). */
  urlDaApi: z.string(),
  chaveDeApi: EncryptedValueSchema,
  /** Os últimos caracteres da chave, para a tela reconhecer qual está salva. */
  finalDaChave: z.string().max(8),
  /** A conta (o subdomínio da URL) e quem é o dono da chave, segundo o próprio ActiveCampaign. */
  conta: z.string(),
  usuarioDaChave: z.string().nullable().optional(),
  validadaEm: z.coerce.date(),
  configuradaPor: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type ConexaoActiveCampaign = z.infer<typeof ConexaoActiveCampaignSchema>;

/** O que a tela vê. Nunca a chave. */
export interface SituacaoDaConexaoActiveCampaign {
  configurada: boolean;
  urlDaApi: string | null;
  conta: string | null;
  usuarioDaChave: string | null;
  finalDaChave: string | null;
  validadaEm: string | null;
}

export function paraSituacaoDaConexaoActiveCampaign(c: ConexaoActiveCampaign | null | undefined): SituacaoDaConexaoActiveCampaign {
  if (!c || c.deletedAt) return { configurada: false, urlDaApi: null, conta: null, usuarioDaChave: null, finalDaChave: null, validadaEm: null };
  return {
    configurada: true,
    urlDaApi: c.urlDaApi,
    conta: c.conta,
    usuarioDaChave: c.usuarioDaChave ?? null,
    finalDaChave: c.finalDaChave,
    validadaEm: new Date(c.validadaEm).toISOString(),
  };
}

// --- Regras ------------------------------------------------------------------

/**
 * Para onde vai um valor no CRM. E-mail, nome e telefone são os campos fixos
 * do contato (o e-mail é a chave: contato com o mesmo e-mail é atualizado, não
 * duplicado). Os personalizados levam o id do campo no ActiveCampaign e o
 * tipo, que decide o formato (`||a||` no contato, lista no negócio, para os de
 * múltipla escolha).
 */
export const DestinoNoActiveCampaignEnum = z.enum(['EMAIL', 'NOME', 'TELEFONE', 'CAMPO_DO_CONTATO', 'CAMPO_DO_NEGOCIO']);
export type DestinoNoActiveCampaign = z.infer<typeof DestinoNoActiveCampaignEnum>;

/**
 * De onde vem o valor: um campo do formulário (pelo `id` do campo, estável
 * entre versões), um dado da conta de quem enviou — no app ela está logada,
 * então nome, e-mail e telefone não precisam ser perguntados — ou um texto
 * fixo (ex.: "App Eva Maternidade" num campo de origem).
 */
export const OrigemDoValorEnum = z.enum(['CAMPO_DO_FORMULARIO', 'CONTA', 'TEXTO']);
export const DadoDaContaEnum = z.enum(['NOME', 'EMAIL', 'TELEFONE']);

export const MapeamentoActiveCampaignSchema = z.object({
  destino: DestinoNoActiveCampaignEnum,
  /** Nos personalizados: o id do campo no ActiveCampaign, o nome (para a tela) e o tipo. */
  campoId: z.string().max(40).optional(),
  campoNome: z.string().max(200).optional(),
  campoTipo: z.string().max(40).optional(),
  origem: OrigemDoValorEnum,
  /** Em CAMPO_DO_FORMULARIO: o `id` do campo do formulário. */
  campoDoFormulario: z.string().max(100).optional(),
  /** Em CONTA. */
  dadoDaConta: DadoDaContaEnum.optional(),
  /** Em TEXTO. */
  texto: z.string().max(500).optional(),
  /** Para campo sim/não do formulário: o texto que vai em cada caso. Sem eles, "Sim" e "Não". */
  seSim: z.string().max(200).optional(),
  seNao: z.string().max(200).optional(),
});
export type MapeamentoActiveCampaign = z.infer<typeof MapeamentoActiveCampaignSchema>;

export const RegraActiveCampaignSchema = z.object({
  id: z.string(),
  brandId: brandIdField(),
  formId: z.string(),
  ativa: z.boolean().default(true),
  funilId: z.string(),
  funilNome: z.string().optional(),
  etapaId: z.string(),
  etapaNome: z.string().optional(),
  /** O dono do negócio no ActiveCampaign (a API exige um). */
  responsavelId: z.string(),
  responsavelNome: z.string().optional(),
  /** Título do negócio: o prefixo e, opcionalmente, o valor de um campo do formulário ("Indicação: {Empresa}"). */
  tituloPrefixo: z.string().max(120).default(''),
  tituloCampo: z.string().max(100).nullable().optional(),
  /** Uma tag acrescentada ao contato — o que dispara automações por "tag adicionada" no CRM. */
  tagId: z.string().nullable().optional(),
  tagNome: z.string().nullable().optional(),
  /** Todas as respostas também vão na descrição do negócio: cada negócio guarda a própria indicação. */
  respostasNaDescricao: z.boolean().default(true),
  mapeamentos: z.array(MapeamentoActiveCampaignSchema).max(60).default([]),
  criadaPor: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type RegraActiveCampaign = z.infer<typeof RegraActiveCampaignSchema>;

// --- Envios ------------------------------------------------------------------

/**
 * ENVIANDO é gravado antes das chamadas: se o servidor parar no meio (o Cloud
 * Run congela a CPU fora de requisição), o envio fica visível como não
 * confirmado, em vez de sumir.
 */
export const SituacaoDoEnvioActiveCampaignEnum = z.enum(['ENVIANDO', 'ENVIADO', 'ERRO']);

export const EnvioActiveCampaignSchema = z.object({
  /** O id da resposta: uma resposta, um envio — o reenvio atualiza o mesmo documento. */
  id: z.string(),
  brandId: brandIdField(),
  regraId: z.string(),
  formId: z.string(),
  situacao: SituacaoDoEnvioActiveCampaignEnum,
  contatoId: z.string().nullable().optional(),
  /** Com o negócio criado, o reenvio não cria outro. */
  negocioId: z.string().nullable().optional(),
  erro: z.string().max(500).nullable().optional(),
  tentativas: z.number().int().min(1).default(1),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type EnvioActiveCampaign = z.infer<typeof EnvioActiveCampaignSchema>;
